import { CSSProperties, FC, JSX, useEffect, useState } from "react";
import "./App.css";

import {
  ConfigProvider,
  Layout,
  Menu,
  Button,
  Typography,
  Row,
  Col,
  Card,
  Space,
  Tag,
  Image,
  Form,
  Input,
  FloatButton,
  notification,
  Grid,
  Drawer,
  theme as antdTheme,
} from "antd";

import {
  ThunderboltOutlined,
  AppstoreOutlined,
  ShoppingCartOutlined,
  SafetyCertificateOutlined,
  BarChartOutlined,
  MedicineBoxOutlined,
  ToolOutlined,
  ShoppingOutlined,
  ShopOutlined,
  InboxOutlined,
  FileProtectOutlined,
  DollarOutlined,
  MessageOutlined,
  FacebookFilled,
  MenuOutlined,
  CheckCircleFilled,
  ArrowRightOutlined,
  DeploymentUnitOutlined,
  CustomerServiceOutlined,
  ApartmentOutlined,
  AuditOutlined,
  StockOutlined,
  ClusterOutlined,
  MoonOutlined,
  SunOutlined,
} from "@ant-design/icons";
import Peticion from "./clases/Peticion";
import Funciones from "./clases/Funciones";

const { Header, Content, Footer } = Layout;
const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

const WHATSAPP_URL = "https://wa.me/50257822762";
const FACEBOOK_URL = "https://facebook.com/share/1FSzGfJijw";
const TEMA_STORAGE_KEY = "data-systems-gt-theme";
const COLOR_PRIMARIO = "#075fc9";
const COLOR_SECUNDARIO_LOGO = "#00b7e5";

type TemaVisual = "claro" | "oscuro";

const obtenerTemaInicial = (): TemaVisual => {
  if (typeof window === "undefined") return "claro";
  return window.localStorage.getItem(TEMA_STORAGE_KEY) === "oscuro" ? "oscuro" : "claro";
};

const secciones = [
  { id: "hero", label: "Inicio" },
  { id: "quienes-somos", label: "Quiénes somos" },
  { id: "producto", label: "Producto" },
  { id: "clientes", label: "Clientes" },
  { id: "galeria", label: "Galería" },
  { id: "contacto", label: "Contacto" },
];

const resumenHero = [
  {
    title: "1 sola plataforma",
    description: "Centraliza ventas, inventario, compras, caja y FEL sin depender de hojas dispersas.",
  },
  {
    title: "Visibilidad diaria",
    description: "Consulta existencias, movimientos y reportes sin esperar cierres manuales.",
  },
  {
    title: "Operación más ordenada",
    description: "Reduce errores de captura y mejora la trazabilidad de cada proceso.",
  },
];

const mensajesHero = [
  "Inventario, ventas, caja, compras y facturación electrónica en un solo sistema.",
  "Implementación guiada según tu operación y tus procesos reales.",
  "Soporte cercano para que tu equipo adopte la herramienta con confianza.",
];

const bloquesEmpresa = [
  {
    icon: <DeploymentUnitOutlined />,
    title: "Qué hacemos",
    description:
      "Diseñamos e implementamos software empresarial para negocios que necesitan ordenar inventario, ventas, compras, caja y facturación electrónica.",
  },
  {
    icon: <ClusterOutlined />,
    title: "Cómo trabajamos",
    description:
      "Entendemos tu operación, configuramos el sistema a tus flujos y te acompañamos en la puesta en marcha para que el cambio sí funcione.",
  },
  {
    icon: <SafetyCertificateOutlined />,
    title: "Por qué confiar en nosotros",
    description:
      "Combinamos desarrollo, soporte y criterio operativo para que la tecnología aporte control real, no complejidad adicional.",
  },
];

const diferenciales = [
  {
    icon: <DeploymentUnitOutlined />,
    title: "Software a medida",
    description: "Ajustamos la solución a la realidad de tu negocio y a tu forma de operar.",
  },
  {
    icon: <CustomerServiceOutlined />,
    title: "Soporte cercano",
    description: "Te acompañamos antes, durante y después de la implementación.",
  },
  {
    icon: <ApartmentOutlined />,
    title: "Soluciones escalables",
    description: "Crece por sucursal, caja o línea de producto sin perder control.",
  },
  {
    icon: <AuditOutlined />,
    title: "Procesos reales",
    description: "Pensado para compras, ventas, inventarios y trazabilidad diaria.",
  },
];

const modulos = [
  {
    icon: <InboxOutlined />,
    title: "Inventario en tiempo real",
    description: "Mantén existencias, movimientos y costos bajo control en cada sucursal.",
    benefits: [
      "Kardex, lotes y fechas de vencimiento",
      "Alertas por mínimos, máximos y existencias críticas",
      "Traslados, ajustes y trazabilidad de movimientos",
    ],
  },
  {
    icon: <ShoppingCartOutlined />,
    title: "Ventas y POS",
    description: "Agiliza la atención en caja y registra ventas con menos fricción.",
    benefits: [
      "Cobros, cotizaciones y cuentas por cobrar",
      "Lectura de códigos de barras y flujos rápidos en mostrador",
      "Historial comercial por cliente y comprobante",
    ],
  },
  {
    icon: <AppstoreOutlined />,
    title: "Compras y proveedores",
    description: "Ordena abastecimiento, costos y seguimiento de documentos por pagar.",
    benefits: [
      "Órdenes de compra y recepción de mercadería",
      "Control de costos, devoluciones y ajustes",
      "Mejor visibilidad sobre proveedores y reposición",
    ],
  },
  {
    icon: <DollarOutlined />,
    title: "Caja y arqueos",
    description: "Supervisa aperturas, cierres y movimientos para reducir diferencias.",
    benefits: [
      "Arqueos, retiros, ingresos y cierres por caja",
      "Control por usuario, turno o sucursal",
      "Integración natural con el flujo de ventas",
    ],
  },
  {
    icon: <BarChartOutlined />,
    title: "Reportes y dashboards",
    description: "Convierte la operación diaria en información útil para decidir mejor.",
    benefits: [
      "Ventas, márgenes, existencias y rotación",
      "Exportación a Excel o PDF para compartir fácilmente",
      "Lectura clara para gerencia y seguimiento operativo",
    ],
  },
  {
    icon: <FileProtectOutlined />,
    title: "Facturación electrónica",
    description: "Emite comprobantes con cumplimiento y trazabilidad desde el mismo sistema.",
    benefits: [
      "Integración con conectores locales de facturación",
      "PDF, envío por correo y consulta posterior",
      "Menos doble digitación y mejor auditoría documental",
    ],
  },
];

const industrias = [
  {
    icon: <MedicineBoxOutlined />,
    title: "Farmacias",
    description: "Controla lotes, vencimientos, existencias críticas y ventas diarias desde una sola plataforma.",
    tags: ["Lotes", "Vencimientos", "Stock crítico"],
  },
  {
    icon: <ShoppingOutlined />,
    title: "Abarroterías",
    description: "Agiliza caja, reposición y control de precios para vender con más orden y menos pérdidas.",
    tags: ["POS", "Reposición", "Precios"],
  },
  {
    icon: <ToolOutlined />,
    title: "Ferreterías",
    description: "Administra productos con variantes, cotizaciones y existencias por unidad o presentación.",
    tags: ["Variantes", "Cotizaciones", "Inventario"],
  },
  {
    icon: <ShopOutlined />,
    title: "Supermercados",
    description: "Coordina múltiples cajas, alto volumen de ventas y control por área sin perder trazabilidad.",
    tags: ["Multi-caja", "Alto volumen", "Control diario"],
  },
  {
    icon: <StockOutlined />,
    title: "Distribuidoras",
    description: "Ordena compras, inventarios, cuentas por cobrar y seguimiento comercial con mayor visibilidad.",
    tags: ["Cobranza", "Rutas comerciales", "Abastecimiento"],
  },
  {
    icon: <ApartmentOutlined />,
    title: "Múltiples sucursales",
    description: "Consolida operación, transferencias y reportes por sede desde una sola base de control.",
    tags: ["Sucursales", "Traslados", "Reportes consolidados"],
  },
];

const demostraciones = [
  {
    src: "/imagenes/Ventas.png",
    categoria: "Ventas y caja",
    title: "Módulo de ventas",
    description: "Consulta ventas, cobros y seguimiento diario desde una interfaz operativa y clara.",
  },
  {
    src: "/imagenes/DocumentosPorCobrar.png",
    categoria: "Cobranza",
    title: "Cuentas por cobrar",
    description: "Da seguimiento a documentos pendientes y mantén mejor control sobre la recuperación de cartera.",
  },
  {
    src: "/imagenes/Kardex.png",
    categoria: "Inventario",
    title: "Kardex de inventario",
    description: "Visualiza movimientos, entradas y salidas con la trazabilidad que necesitas para auditar.",
  },
  {
    src: "/imagenes/FEL.png",
    categoria: "Cumplimiento",
    title: "Facturación electrónica",
    description: "Emite documentos electrónicos desde el mismo flujo de venta con respaldo y orden documental.",
  },
  {
    src: "/imagenes/Compras.png",
    categoria: "Abastecimiento",
    title: "Compras",
    description: "Gestiona abastecimiento, costos y recepción de mercadería con mejor seguimiento operativo.",
  },
  {
    src: "/imagenes/Reportes.png",
    categoria: "Gerencia",
    title: "Reportes gerenciales",
    description: "Convierte la operación diaria en reportes útiles para medir resultados y tomar decisiones.",
  },
];

const motivosContacto = [
  {
    title: "Respuesta rápida por WhatsApp",
    description: "Resolvemos tus dudas sin vueltas y coordinamos la siguiente acción contigo.",
  },
  {
    title: "Demo personalizada",
    description: "Te mostramos el sistema con enfoque en los procesos que más importan en tu negocio.",
  },
  {
    title: "Asesoría sin costo",
    description: "Analizamos tu operación y te orientamos sobre la mejor forma de implementarlo.",
  },
];

type EncabezadoSeccionProps = {
  eyebrow: string;
  title: string;
  description: string;
  centered?: boolean;
};

const EncabezadoSeccion: FC<EncabezadoSeccionProps> = ({
  eyebrow,
  title,
  description,
  centered = false,
}): JSX.Element => (
  <div className={`section-header ${centered ? "section-header--centered" : ""}`}>
    <Tag bordered={false} className="section-eyebrow">
      {eyebrow}
    </Tag>
    <Title level={2} className="section-title">
      {title}
    </Title>
    <Paragraph className="section-description">{description}</Paragraph>
  </div>
);

const estiloAnimacion = (delay = 0): CSSProperties =>
  ({ ["--delay" as any]: `${delay}ms` } as CSSProperties);

const App: FC<any> = (): JSX.Element => {
  const [api, contextHolder] = notification.useNotification();
  const [procesando, setProcesando] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [navCompacto, setNavCompacto] = useState(false);
  const [seccionActiva, setSeccionActiva] = useState("hero");
  const [tema, setTema] = useState<TemaVisual>(obtenerTemaInicial);
  const screens = useBreakpoint();
  const [form] = Form.useForm();
  const temaOscuro = tema === "oscuro";

  useEffect(() => {
    document.documentElement.dataset.theme = tema;
    window.localStorage.setItem(TEMA_STORAGE_KEY, tema);

    const metaThemeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    metaThemeColor?.setAttribute("content", temaOscuro ? "#07111f" : "#ffffff");
  }, [tema, temaOscuro]);

  useEffect(() => {
    const elementos = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (reducedMotion.matches) {
      elementos.forEach((elemento) => elemento.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.18,
        rootMargin: "0px 0px -10% 0px",
      }
    );

    elementos.forEach((elemento) => observer.observe(elemento));

    return () => observer.disconnect();
  }, [tema]);

  useEffect(() => {
    const handleScroll = () => {
      setNavCompacto(window.scrollY > 18);
    };

    const sections = secciones
      .map((section) => document.getElementById(section.id))
      .filter(Boolean) as HTMLElement[];

    const observer = new IntersectionObserver(
      (entries) => {
        const visibles = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visibles.length > 0) {
          setSeccionActiva(visibles[0].target.id);
        }
      },
      {
        threshold: [0.2, 0.4, 0.6],
        rootMargin: "-45% 0px -45% 0px",
      }
    );

    sections.forEach((section) => observer.observe(section));
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    setNavOpen(false);

    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const enviarCorreo = () => {
    if (procesando) return;
    setProcesando(true);

    const formData = new FormData();
    formData.set("txtAsunto", "Solicitud de demo");
    formData.set("txtNombre", form.getFieldValue("txtNombre") || "");
    formData.set("txtCorreo", form.getFieldValue("txtCorreo") || "");

    if (form.getFieldValue("txtEmpresa")) {
      formData.set("txtEmpresa", form.getFieldValue("txtEmpresa"));
    }

    formData.set("txtDescripcion", form.getFieldValue("txtDescripcion") || "");

    new Peticion()
      .POST<any>("/api/correo", formData)
      .then(() => {
        Funciones.mostrarNotificacion(api, "¡Gracias!", "Te contactaremos pronto para coordinar tu demo.", "success");
        form.resetFields();
      })
      .catch((err) => {
        if (err.response && err.response.status === 400 && err.response.data.mensaje) {
          Funciones.mostrarNotificacion(api, "Error", err.response.data.mensaje, "error");
        } else {
          Funciones.mostrarNotificacion(api, "Error", "Ha ocurrido un error al realizar esta acción", "error");
        }
      })
      .finally(() => {
        setProcesando(false);
      });
  };

  const menuItems = secciones.map((item) => ({
    key: item.id,
    label: item.label,
    onClick: () => scrollToId(item.id),
  }));

  const alternarTema = () => {
    setTema((temaActual) => (temaActual === "claro" ? "oscuro" : "claro"));
  };

  const etiquetaTema = temaOscuro ? "Activar tema claro" : "Activar tema oscuro";

  return (
    <ConfigProvider
      theme={{
        algorithm: temaOscuro ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: temaOscuro ? "#2568bd" : COLOR_PRIMARIO,
          colorInfo: temaOscuro ? "#48cfff" : COLOR_SECUNDARIO_LOGO,
          colorLink: temaOscuro ? "#48cfff" : COLOR_PRIMARIO,
          borderRadius: 22,
          fontFamily: '"Inter", sans-serif',
          colorBgBase: temaOscuro ? "#07111f" : "#ffffff",
          colorText: temaOscuro ? "#edf6ff" : "#132238",
          colorTextSecondary: temaOscuro ? "#a9bbcf" : "#58677c",
          colorBorder: temaOscuro ? "#294057" : "#d5e0ec",
        },
        components: {
          Layout: {
            headerBg: "transparent",
            bodyBg: "transparent",
            footerBg: "transparent",
          },
          Button: {
            controlHeightLG: 52,
            fontWeight: 600,
            paddingInlineLG: 24,
          },
          Card: {
            borderRadiusLG: 28,
          },
          Input: {
            controlHeight: 52,
          },
          Menu: {
            itemBg: "transparent",
            itemSelectedBg: temaOscuro ? "rgba(72, 207, 255, 0.14)" : "rgba(0, 183, 229, 0.12)",
            itemBorderRadius: 999,
          },
        },
      }}
    >
      {contextHolder}
      <Layout className={`pagina-principal tema-${tema}`}>
        <Header className={`site-header ${navCompacto ? "site-header--compact" : ""}`}>
          <div className="nav-shell">
            <button type="button" className="brand" onClick={() => scrollToId("hero")}>
              <span className="brand-mark brand-mark--image">
                <img src="/logo192.png" alt="Logo Data Systems GT" className="brand-logo" />
              </span>
              <span className="brand-copy">
                <strong>Data Systems GT</strong>
                <small>Software empresarial</small>
              </span>
            </button>

            {screens.md ? (
              <Menu mode="horizontal" className="nav-menu" selectedKeys={[seccionActiva]} items={menuItems} />
            ) : (
              <div className="nav-spacer" />
            )}

            <div className="nav-actions">
              <Button
                type="text"
                className="theme-toggle"
                icon={temaOscuro ? <SunOutlined /> : <MoonOutlined />}
                onClick={alternarTema}
                aria-label={etiquetaTema}
                aria-pressed={temaOscuro}
              />

              {screens.md ? (
                <Button type="primary" className="nav-cta" onClick={() => scrollToId("contacto")}>
                  Solicitar demo
                </Button>
              ) : (
                <Button
                  className="mobile-menu-trigger"
                  icon={<MenuOutlined />}
                  onClick={() => setNavOpen(true)}
                  aria-label="Abrir menú"
                />
              )}
            </div>
          </div>
        </Header>

        <Drawer
          title="Navegación"
          placement="right"
          open={navOpen}
          onClose={() => setNavOpen(false)}
          className={`mobile-drawer tema-${tema}`}
          rootClassName={`mobile-drawer-root tema-${tema}`}
          width={320}
          styles={{ body: { padding: 0 } }}
        >
          <Menu mode="inline" className="mobile-menu" selectedKeys={[seccionActiva]} items={menuItems} />
          <div className="mobile-drawer__footer">
            <Button type="primary" block size="large" onClick={() => scrollToId("contacto")}>
              Solicitar demo
            </Button>
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <Button block size="large" icon={<MessageOutlined />} href={WHATSAPP_URL} target="_blank">
                Escribir por WhatsApp
              </Button>
              <Button block size="large" icon={<FacebookFilled />} href={FACEBOOK_URL} target="_blank">
                Ver Facebook
              </Button>
            </Space>
          </div>
        </Drawer>

        <Content className="site-content">
          <section id="hero" className="hero-section section-anchor">
            <div className="hero-glow hero-glow--one" />
            <div className="hero-glow hero-glow--two" />
            <div className="section-inner hero-grid">
              <div className="hero-copy reveal reveal-left">
                <Tag bordered={false} className="hero-eyebrow">
                  Software para control comercial y facturación electrónica
                </Tag>
                <Title className="hero-title">
                  Centraliza ventas, inventario y facturación electrónica con más control para tu negocio
                </Title>
                <Paragraph className="hero-lead">
                  Inventario, ventas, caja, compras y facturación electrónica en un solo sistema.
                </Paragraph>
                <Paragraph className="hero-description">
                  Diseñado para negocios que necesitan operar con menos errores, mejor trazabilidad y reportes claros
                  para tomar decisiones a tiempo.
                </Paragraph>

                <div className="hero-checklist">
                  {mensajesHero.map((item, index) => (
                    <div
                      key={item}
                      className="hero-checklist__item reveal reveal-up"
                      style={estiloAnimacion(80 + index * 70)}
                    >
                      <CheckCircleFilled />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>

                <Space wrap size={14} className="hero-actions">
                  <Button
                    size="large"
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    className="button-primary"
                    onClick={() => scrollToId("contacto")}
                  >
                    Solicitar demo
                  </Button>
                  <Button
                    size="large"
                    icon={<ArrowRightOutlined />}
                    className="button-secondary"
                    onClick={() => scrollToId("producto")}
                  >
                    Ver funcionalidades
                  </Button>
                  <Button
                    size="large"
                    icon={<MessageOutlined />}
                    className="button-whatsapp"
                    href={WHATSAPP_URL}
                    target="_blank"
                  >
                    Escribir por WhatsApp
                  </Button>
                </Space>

                <div className="hero-summary-grid">
                  {resumenHero.map((item, index) => (
                    <Card
                      key={item.title}
                      className="hero-summary-card reveal reveal-up"
                      style={estiloAnimacion(120 + index * 80)}
                      variant="borderless"
                    >
                      <Text className="hero-summary-card__title">{item.title}</Text>
                      <Paragraph className="hero-summary-card__description">{item.description}</Paragraph>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="hero-visual reveal reveal-right">
                <div className="hero-window">
                  <div className="hero-window__topbar">
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="hero-window__content">
                    <div className="hero-window__label">
                      <span className="hero-window__indicator" />
                      Vista del sistema en operación
                    </div>
                    <Image
                      src="/imagenes/Ventas.png"
                      alt="Vista del módulo de ventas"
                      preview={false}
                      className="hero-window__image"
                    />
                  </div>
                </div>

                <Card className="hero-floating hero-floating--top" variant="borderless">
                  <Text className="hero-floating__eyebrow">Inventario y trazabilidad</Text>
                  <Title level={4}>Kardex con movimientos claros</Title>
                  <Image
                    src="/imagenes/Kardex.png"
                    alt="Vista del kardex de inventario"
                    preview={false}
                    className="hero-floating__image"
                  />
                </Card>

                <Card className="hero-floating hero-floating--bottom" variant="borderless">
                  <Text className="hero-floating__eyebrow">Módulos conectados</Text>
                  <div className="hero-chip-group">
                    <span>Ventas</span>
                    <span>Compras</span>
                    <span>Caja</span>
                    <span>FEL</span>
                  </div>
                  <Paragraph>
                    La información fluye entre procesos para evitar dobles registros y pérdida de control.
                  </Paragraph>
                </Card>
              </div>
            </div>
          </section>

          <section id="quienes-somos" className="section section-anchor">
            <div className="section-inner">
              <Row gutter={[28, 28]} align="stretch">
                <Col xs={24} lg={9}>
                  <div className="reveal reveal-left">
                    <EncabezadoSeccion
                      eyebrow="Quiénes somos"
                      title="Tecnología aplicada a procesos reales de negocio"
                      description="Ayudamos a empresas que necesitan más orden operativo a implementar software que les dé visibilidad, trazabilidad y control sin complicar el día a día."
                    />

                    <Card className="trust-panel" variant="borderless">
                      <Text className="trust-panel__eyebrow">Lo que más valoran nuestros clientes</Text>
                      <div className="trust-panel__list">
                        {diferenciales.map((item) => (
                          <div key={item.title} className="trust-panel__item">
                            <span className="trust-panel__icon">{item.icon}</span>
                            <div>
                              <strong>{item.title}</strong>
                              <p>{item.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                </Col>

                <Col xs={24} lg={15}>
                  <Row gutter={[18, 18]}>
                    {bloquesEmpresa.map((item, index) => (
                      <Col xs={24} md={8} key={item.title}>
                        <Card
                          className="info-card reveal reveal-up"
                          variant="borderless"
                          style={estiloAnimacion(index * 90)}
                        >
                          <span className="info-card__icon">{item.icon}</span>
                          <Title level={4}>{item.title}</Title>
                          <Paragraph>{item.description}</Paragraph>
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  <div className="differentials-grid">
                    {diferenciales.map((item, index) => (
                      <Card
                        key={item.title}
                        className="differential-card reveal reveal-up"
                        variant="borderless"
                        style={estiloAnimacion(120 + index * 70)}
                      >
                        <span className="differential-card__icon">{item.icon}</span>
                        <div>
                          <strong>{item.title}</strong>
                          <p>{item.description}</p>
                        </div>
                      </Card>
                    ))}
                  </div>
                </Col>
              </Row>
            </div>
          </section>

          <section id="producto" className="section section-muted section-anchor">
            <div className="section-inner">
              <Row gutter={[28, 28]} align="top">
                <Col xs={24} lg={8}>
                  <div className="reveal reveal-left">
                    <EncabezadoSeccion
                      eyebrow="Producto"
                      title="Control de inventario y facturación electrónica con enfoque operativo"
                      description="Centraliza procesos críticos en una plataforma pensada para que tu equipo trabaje con más orden, menos errores y mejor información."
                    />

                    <Card className="product-spotlight" variant="borderless">
                      <Tag bordered={false} className="product-spotlight__tag">
                        Solución principal
                      </Tag>
                      <Title level={3}>Un sistema listo para tu operación diaria</Title>
                      <Paragraph>
                        Ideal para negocios que necesitan vender, comprar, controlar existencias, administrar caja y
                        facturar desde un solo lugar.
                      </Paragraph>
                      <div className="product-spotlight__list">
                        <div>
                          <CheckCircleFilled />
                          <span>Menos reprocesos al capturar la información una sola vez.</span>
                        </div>
                        <div>
                          <CheckCircleFilled />
                          <span>Más trazabilidad para auditar movimientos, documentos y cierres.</span>
                        </div>
                        <div>
                          <CheckCircleFilled />
                          <span>Mejores reportes para gerencia, ventas, compras y bodega.</span>
                        </div>
                      </div>
                      <Space wrap>
                        <Button type="primary" size="large" onClick={() => scrollToId("contacto")}>
                          Solicitar demo
                        </Button>
                        <Button size="large" onClick={() => scrollToId("galeria")}>
                          Ver demostraciones
                        </Button>
                      </Space>
                    </Card>
                  </div>
                </Col>

                <Col xs={24} lg={16}>
                  <Row gutter={[18, 18]}>
                    {modulos.map((item, index) => (
                      <Col xs={24} md={12} xl={8} key={item.title}>
                        <Card
                          className="feature-card reveal reveal-up"
                          variant="borderless"
                          style={estiloAnimacion(index * 75)}
                        >
                          <span className="feature-card__icon">{item.icon}</span>
                          <Title level={4}>{item.title}</Title>
                          <Paragraph>{item.description}</Paragraph>
                          <div className="feature-benefits">
                            {item.benefits.map((benefit) => (
                              <div key={benefit} className="feature-benefit">
                                <CheckCircleFilled />
                                <span>{benefit}</span>
                              </div>
                            ))}
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Col>
              </Row>
            </div>
          </section>

          <section id="clientes" className="section section-anchor">
            <div className="section-inner">
              <EncabezadoSeccion
                eyebrow="¿Para quién es?"
                title="Pensado para negocios que necesitan más control operativo"
                description="La plataforma se adapta especialmente bien a operaciones comerciales con inventario, caja, compras y necesidad de trazabilidad diaria."
                centered
              />

              <Row gutter={[18, 18]}>
                {industrias.map((item, index) => (
                  <Col xs={24} md={12} xl={8} key={item.title}>
                    <Card
                      className="industry-card reveal reveal-up"
                      variant="borderless"
                      style={estiloAnimacion(index * 70)}
                    >
                      <span className="industry-card__icon">{item.icon}</span>
                      <Title level={4}>{item.title}</Title>
                      <Paragraph>{item.description}</Paragraph>
                      <div className="industry-tags">
                        {item.tags.map((tag) => (
                          <Tag key={tag} bordered={false}>
                            {tag}
                          </Tag>
                        ))}
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          </section>

          <section id="galeria" className="section section-muted section-anchor">
            <div className="section-inner">
              <EncabezadoSeccion
                eyebrow="Demostraciones"
                title="Explora cómo se ve el sistema en módulos clave"
                description="Las capturas están organizadas por proceso para que identifiques rápidamente cómo se adapta a tu operación."
                centered
              />

              <Image.PreviewGroup key={tema}>
                <Row gutter={[18, 18]}>
                  {demostraciones.map((item, index) => (
                    <Col xs={24} sm={12} xl={8} key={item.title}>
                      <Card
                        className="gallery-card reveal reveal-up"
                        variant="borderless"
                        style={estiloAnimacion(index * 70)}
                      >
                        <div className="gallery-card__media">
                          <span className="gallery-card__badge">{item.categoria}</span>
                          <Image
                            src={item.src}
                            alt={item.title}
                            className="gallery-card__image"
                            preview={{ mask: <span>Ver imagen</span> }}
                          />
                        </div>
                        <div className="gallery-card__content">
                          <Title level={4}>{item.title}</Title>
                          <Paragraph>{item.description}</Paragraph>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Image.PreviewGroup>
            </div>
          </section>

          <section id="contacto" className="section section-anchor contact-section">
            <div className="section-inner">
              <Row gutter={[28, 28]} align="stretch">
                <Col xs={24} lg={10}>
                  <div className="reveal reveal-left">
                    <EncabezadoSeccion
                      eyebrow="Contacto"
                      title="Agenda una demo y descubre cómo se adapta a tu negocio"
                      description="Cuéntanos sobre tu operación y te mostraremos cómo el sistema puede ayudarte a vender con más control, reducir errores y tener mejores reportes."
                    />

                    <div className="contact-highlights">
                      {motivosContacto.map((item, index) => (
                        <Card
                          key={item.title}
                          className="contact-highlight reveal reveal-up"
                          variant="borderless"
                          style={estiloAnimacion(index * 80)}
                        >
                          <strong>{item.title}</strong>
                          <p>{item.description}</p>
                        </Card>
                      ))}
                    </div>

                    <div className="social-actions">
                      <Button
                        size="large"
                        icon={<MessageOutlined />}
                        className="social-button social-button--whatsapp"
                        href={WHATSAPP_URL}
                        target="_blank"
                      >
                        WhatsApp
                      </Button>
                      <Button
                        size="large"
                        icon={<FacebookFilled />}
                        className="social-button social-button--facebook"
                        href={FACEBOOK_URL}
                        target="_blank"
                      >
                        Facebook
                      </Button>
                    </div>
                  </div>
                </Col>

                <Col xs={24} lg={14}>
                  <Card className="form-card reveal reveal-right" variant="borderless">
                    <div className="form-card__header">
                      <Tag bordered={false} className="section-eyebrow">
                        Demo personalizada
                      </Tag>
                      <Title level={3}>Cuéntanos sobre tu negocio</Title>
                      <Paragraph>
                        Déjanos tus datos y te contactaremos para mostrarte cómo la plataforma puede adaptarse a tu
                        operación.
                      </Paragraph>
                    </div>

                    <Form form={form} layout="vertical" requiredMark={false} onFinish={enviarCorreo} className="contact-form">
                      <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                          <Form.Item
                            name="txtNombre"
                            label="Nombre"
                            rules={[{ required: true, message: "Ingresa tu nombre" }]}
                          >
                            <Input placeholder="Ej. Carlos Méndez" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item
                            name="txtCorreo"
                            label="Correo"
                            rules={[
                              { required: true, message: "Ingresa tu correo" },
                              { type: "email", message: "Correo no válido" },
                            ]}
                          >
                            <Input placeholder="Ej. ventas@tuempresa.com" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={[16, 0]}>
                        <Col xs={24}>
                          <Form.Item name="txtEmpresa" label="Empresa o negocio">
                            <Input placeholder="Ej. Farmacia Central" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item
                        name="txtDescripcion"
                        label="Cuéntanos qué necesitas"
                        rules={[{ required: true, message: "Cuéntanos qué necesitas" }]}
                      >
                        <Input.TextArea
                          rows={5}
                          placeholder="Ej. Necesito controlar inventario por sucursal, mejorar caja y emitir facturación electrónica."
                        />
                      </Form.Item>

                      <div className="form-card__actions">
                        <Button type="primary" htmlType="submit" loading={procesando} size="large">
                          Quiero mi demo
                        </Button>
                        <Button htmlType="button" size="large" onClick={() => form.resetFields()} disabled={procesando}>
                          Limpiar
                        </Button>
                      </div>

                      <Text className="form-card__footnote">
                        Respuesta inicial por WhatsApp o correo según tu preferencia.
                      </Text>
                    </Form>
                  </Card>
                </Col>
              </Row>
            </div>
          </section>
        </Content>

        <Footer className="site-footer">
          <div className="section-inner footer-shell">
            <div className="footer-brand">
              <span className="brand-mark brand-mark--image brand-mark--footer">
                <img src="/logo192.png" alt="Logo Data Systems GT" className="brand-logo" />
              </span>
              <div>
                <Text strong>Data Systems GT</Text>
                <Text className="footer-copy">
                  Software para control de inventario, ventas, compras, caja y facturación electrónica.
                </Text>
              </div>
            </div>
            <Text className="footer-copy">© {new Date().getFullYear()} Todos los derechos reservados.</Text>
          </div>
        </Footer>

        <FloatButton.Group shape="circle">
          <FloatButton
            icon={<MessageOutlined />}
            tooltip={<div>Escribir por WhatsApp</div>}
            onClick={() => window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer")}
          />
          <FloatButton.BackTop visibilityHeight={320} />
        </FloatButton.Group>
      </Layout>
    </ConfigProvider>
  );
};

export default App;
